import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Crown, Check, Zap, Users, Calendar, CreditCard, Shield, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } };
};

const MODULE_LABELS = { leads: 'Leads', tasks: 'Tasks', sales: 'Sales', service: 'Service', spares: 'Spares' };
const MODULE_COLORS = { leads: 'bg-green-100 text-green-700', tasks: 'bg-blue-100 text-blue-700', sales: 'bg-purple-100 text-purple-700', service: 'bg-orange-100 text-orange-700', spares: 'bg-teal-100 text-teal-700' };

const formatINR = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amount);

export default function CompanySubscription({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [plans, setPlans] = React.useState([]);
  const [currentSub, setCurrentSub] = React.useState(null);
  const [usage, setUsage] = React.useState({});
  const [hasActive, setHasActive] = React.useState(false);
  const [extraUsers, setExtraUsers] = React.useState(0);
  const [payments, setPayments] = React.useState([]);
  const [showPayments, setShowPayments] = React.useState(false);

  React.useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [plansResp, subResp, payResp] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/subscription/plans`, getAuth()),
        axios.get(`${BACKEND_URL}/api/subscription/current`, getAuth()),
        axios.get(`${BACKEND_URL}/api/subscription/payment-history`, getAuth()),
      ]);
      setPlans(plansResp.data.plans || []);
      setCurrentSub(subResp.data.subscription);
      setUsage(subResp.data.usage || {});
      setHasActive(subResp.data.has_active_subscription);
      setPayments(payResp.data.payments || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handleStartTrial = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/subscription/start-trial`, {}, getAuth());
      toast.success('Free trial started!');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start trial');
    }
  };

  const handleSubscribe = async (plan) => {
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/subscription/create-order`, { plan_id: plan.id, extra_users: extraUsers }, getAuth());
      const { order_id, key_id, amount, pricing } = resp.data;

      // Load Razorpay
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        document.head.appendChild(script);
        await new Promise(resolve => { script.onload = resolve; });
      }

      const options = {
        key: key_id,
        amount: amount,
        currency: 'INR',
        name: 'AutoBook Pro',
        description: `${plan.name}${extraUsers > 0 ? ` + ${extraUsers} extra users` : ''}`,
        order_id: order_id,
        handler: async function (response) {
          try {
            await axios.post(`${BACKEND_URL}/api/subscription/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: plan.id,
              extra_users: extraUsers,
            }, getAuth());
            toast.success('Payment successful! Subscription activated.');
            fetchAll();
          } catch (e) {
            toast.error('Payment verification failed. Contact support.');
          }
        },
        prefill: { email: user?.email || '', contact: user?.phone || '' },
        theme: { color: '#16a34a' },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create order');
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" /></div>;
  }

  const paidPlans = plans.filter(p => !p.is_trial);
  const trialPlan = plans.find(p => p.is_trial);
  const isExpired = currentSub?.status === 'expired';
  const isTrial = currentSub?.status === 'trial';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/modules')} className="p-2 hover:bg-gray-100 rounded-lg"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
            <Crown className="w-6 h-6 text-amber-500" />
            <h1 className="text-xl font-bold text-gray-800">Subscription & Billing</h1>
          </div>
          <button onClick={() => setShowPayments(!showPayments)} className="px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-200 flex items-center gap-2" data-testid="payment-history-btn">
            <CreditCard className="w-4 h-4" /> Payment History
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* Current Subscription Status */}
        {currentSub && (
          <div className={`rounded-2xl border-2 p-6 ${currentSub.status === 'active' ? 'border-green-300 bg-green-50' : currentSub.status === 'trial' ? 'border-blue-300 bg-blue-50' : 'border-red-300 bg-red-50'}`} data-testid="current-subscription">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Shield className={`w-5 h-5 ${currentSub.status === 'active' ? 'text-green-600' : currentSub.status === 'trial' ? 'text-blue-600' : 'text-red-500'}`} />
                  <span className={`text-sm font-bold uppercase tracking-wide ${currentSub.status === 'active' ? 'text-green-700' : currentSub.status === 'trial' ? 'text-blue-700' : 'text-red-600'}`}>
                    {currentSub.status === 'trial' ? 'Free Trial' : currentSub.status}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-gray-800">{currentSub.plan_name}</h3>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                  <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {currentSub.start_date} — {currentSub.end_date}</span>
                  <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {usage.users || 0} / {(currentSub.included_users || 5) + (currentSub.extra_users || 0)} users</span>
                </div>
                <div className="flex gap-2 mt-3">
                  {(currentSub.modules || []).map(m => (
                    <span key={m} className={`px-2.5 py-1 rounded-full text-xs font-medium ${MODULE_COLORS[m] || 'bg-gray-100 text-gray-600'}`}>{MODULE_LABELS[m] || m}</span>
                  ))}
                </div>
              </div>
              {currentSub.total_amount > 0 && (
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-800">{formatINR(currentSub.total_amount)}</p>
                  <p className="text-xs text-gray-500">incl. GST</p>
                </div>
              )}
            </div>

            {/* Trial usage bar */}
            {isTrial && currentSub.limits && (
              <div className="mt-4 grid grid-cols-2 gap-4">
                {currentSub.limits.max_leads && (
                  <div>
                    <div className="flex justify-between text-xs text-gray-600 mb-1"><span>Leads</span><span>{usage.leads || 0}/{currentSub.limits.max_leads}</span></div>
                    <div className="w-full bg-gray-200 rounded-full h-2"><div className="bg-green-500 rounded-full h-2 transition-all" style={{ width: `${Math.min(100, ((usage.leads || 0) / currentSub.limits.max_leads) * 100)}%` }} /></div>
                  </div>
                )}
                {currentSub.limits.max_bookings && (
                  <div>
                    <div className="flex justify-between text-xs text-gray-600 mb-1"><span>Bookings</span><span>{usage.bookings || 0}/{currentSub.limits.max_bookings}</span></div>
                    <div className="w-full bg-gray-200 rounded-full h-2"><div className="bg-blue-500 rounded-full h-2 transition-all" style={{ width: `${Math.min(100, ((usage.bookings || 0) / currentSub.limits.max_bookings) * 100)}%` }} /></div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Lockout Banner */}
        {(!hasActive || isExpired) && (
          <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-6 text-center" data-testid="lockout-banner">
            <Zap className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-red-700 mb-2">
              {isExpired ? 'Your subscription has expired' : 'No active subscription'}
            </h2>
            <p className="text-red-600 text-sm mb-4">Subscribe to a plan to continue using AutoBook Pro</p>
            {!currentSub && trialPlan && (
              <button onClick={handleStartTrial} className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 mr-3" data-testid="start-trial-btn">
                Start Free Trial (7 days)
              </button>
            )}
          </div>
        )}

        {/* Extra Users */}
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-800">Additional Users</h3>
              <p className="text-sm text-gray-500">5 users included. Extra users at {formatINR(500)} + 18% GST each/month</p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setExtraUsers(Math.max(0, extraUsers - 1))} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold">-</button>
              <span className="text-lg font-bold w-8 text-center" data-testid="extra-users-count">{extraUsers}</span>
              <button onClick={() => setExtraUsers(extraUsers + 1)} className="w-8 h-8 rounded-full bg-green-100 hover:bg-green-200 text-green-600 font-bold">+</button>
            </div>
          </div>
        </div>

        {/* Plans Grid */}
        <div>
          <h2 className="text-lg font-bold text-gray-800 mb-4">Choose a Plan</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {paidPlans.map(plan => {
              const pricing = plan.pricing || {};
              const isCurrentPlan = currentSub?.plan_id === plan.id && hasActive;
              return (
                <div key={plan.id} className={`bg-white rounded-2xl border-2 p-6 transition-all hover:shadow-lg ${isCurrentPlan ? 'border-green-400 ring-2 ring-green-200' : 'border-gray-200'}`} data-testid={`plan-${plan.duration_type}`}>
                  {plan.discount_percent > 0 && (
                    <div className="flex justify-end mb-2">
                      <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1"><Star className="w-3 h-3" /> Save {plan.discount_percent}%</span>
                    </div>
                  )}
                  <h3 className="text-lg font-bold text-gray-800">{plan.name}</h3>
                  <div className="mt-3 mb-4">
                    <span className="text-3xl font-bold text-gray-900">{formatINR(pricing.total || 0)}</span>
                    <span className="text-sm text-gray-500 ml-1">/ {plan.duration_type === 'monthly' ? 'mo' : plan.duration_type === 'quarterly' ? '3 mo' : 'yr'}</span>
                  </div>

                  {pricing.discount > 0 && (
                    <p className="text-xs text-green-600 mb-2">You save {formatINR(pricing.discount + pricing.extra_users_discount)} ({plan.discount_percent}% off)</p>
                  )}

                  <div className="space-y-2 mb-6">
                    <div className="flex items-center gap-2 text-sm text-gray-600"><Users className="w-4 h-4 text-gray-400" /> {plan.included_users} users included</div>
                    <div className="flex flex-wrap gap-1.5">
                      {plan.modules.map(m => (
                        <span key={m} className={`px-2 py-0.5 rounded text-[10px] font-semibold ${MODULE_COLORS[m]}`}>{MODULE_LABELS[m]}</span>
                      ))}
                    </div>
                    {!plan.limits?.max_leads && <div className="flex items-center gap-2 text-sm text-gray-600"><Check className="w-4 h-4 text-green-500" /> Unlimited leads & bookings</div>}
                  </div>

                  <div className="text-[10px] text-gray-400 mb-3 space-y-0.5">
                    <p>Base: {formatINR(pricing.base_total || 0)} {pricing.discount > 0 && `- ${formatINR(pricing.discount)} discount`}</p>
                    {extraUsers > 0 && <p>+{extraUsers} users: {formatINR(pricing.extra_users_total || 0)}</p>}
                    <p>GST ({pricing.gst_percent}%): {formatINR(pricing.gst || 0)}</p>
                  </div>

                  <button onClick={() => handleSubscribe(plan)} disabled={isCurrentPlan} className={`w-full py-2.5 rounded-xl font-semibold text-sm ${isCurrentPlan ? 'bg-green-100 text-green-700 cursor-default' : 'bg-green-600 text-white hover:bg-green-700'}`} data-testid={`subscribe-${plan.duration_type}`}>
                    {isCurrentPlan ? 'Current Plan' : 'Subscribe Now'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Payment History */}
        {showPayments && (
          <div className="bg-white rounded-xl border p-6" data-testid="payment-history">
            <h3 className="font-bold text-gray-800 mb-4">Payment History</h3>
            {payments.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-4">No payments yet</p>
            ) : (
              <div className="space-y-2">
                {payments.map(p => (
                  <div key={p.id} className="flex items-center justify-between py-2 border-b">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{p.plan_name}</p>
                      <p className="text-xs text-gray-500">{new Date(p.created_at).toLocaleDateString('en-IN')} — {p.razorpay_payment_id}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-800">{formatINR(p.amount)}</p>
                      <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{p.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
